import type { StatId } from '../domain/stats.ts';
import type { Roller } from './dice.ts';
import type { DiceSpec, RankDamageBand } from './damageDice.ts';
import { addDice, parseDice, rankDamageBonus, rollDice } from './damageDice.ts';
import type { Degree } from './degrees.ts';

/**
 * DAMAGE ROLL - how much did it hurt?
 *
 * Separate from attack resolution (rules/attackResolution.ts) and from
 * mitigation (rules/damage.ts). This module produces the raw damage total; the
 * target then applies DR, resistance, vulnerability and immunity to it.
 *
 * RULE DCC-DMG-005 - C&C p.22 and p.36.
 * Damage is the base damage dice plus the appropriate Stat Mod. The damage Stat
 * does not always match the Stat used to hit, and not every attack adds one.
 *
 * RULE DCC-DMG-006 - C&C pp.22-41.
 * A Rank 5 Upgrade that reads "+XdY base damage" adds those dice to the base
 * damage once the crawler has Rank 5 or more in the Skill.
 *
 * RULE DCC-DMG-007 - Rules for Survival p.15 (Degrees of Success & Failure).
 * On a Critical Hit, roll twice as many BASE damage dice, then add the Stat Mod
 * and other bonuses. On an Amazing Success, add bonus damage equal to the Floor
 * Number (F).
 *
 * RULE DCC-DMG-004 - C&C p.22, Table 14. A Rank damage die is not part of base
 * damage and is therefore never doubled by a Critical Hit.
 */

export interface DamageComponent {
  label: string;
  value: number;
  /** Present when this component came from dice rather than a flat number. */
  faces?: number[];
}

export interface DamageRollInput {
  /** Base damage dice from the attack profile, e.g. "1d6". */
  baseDice: string;
  /** Rank 5 Upgrade dice, applied only when rank >= 5. Null when there is none. */
  rank5Dice: string | null;
  rank: number;
  /** Stat Mod added to damage, already resolved. Null when the attack adds none. */
  damageStat: StatId | null;
  damageStatAbbr: string | null;
  damageStatMod: number | null;
  damageType: string;
  /** Degree of the attack Check. Null when the GM did not reveal the outcome. */
  degree: Degree | null;
  /** Floor Number, for the Amazing Success bonus. */
  floor: number;
  /** Extra Rank damage dice granted by an effect. Table 14 band, not automatic. */
  rankDamageDieTable?: readonly RankDamageBand[];
  grantsRankDamageDie?: boolean;
  /** Flat additions from Damage Effects or Buffs. */
  extraComponents?: readonly DamageComponent[];
}

export interface DamageRollResult {
  /** Dice actually rolled for base damage, after Rank 5 and Critical doubling. */
  baseDiceRolled: DiceSpec;
  components: DamageComponent[];
  total: number;
  damageType: string;
  criticalHit: boolean;
}

export function baseDamageDiceFor(input: {
  baseDice: string;
  rank5Dice: string | null;
  rank: number;
  criticalHit: boolean;
}): DiceSpec {
  let spec = parseDice(input.baseDice);

  // Rank 5 Upgrade dice become part of base damage.
  if (input.rank >= 5 && input.rank5Dice) {
    spec = addDice(spec, parseDice(input.rank5Dice));
  }
  // Critical Hit: twice as many base damage dice.
  if (input.criticalHit) {
    spec = { count: spec.count * 2, sides: spec.sides };
  }
  return spec;
}

export function rollDamage(roller: Roller, input: DamageRollInput): DamageRollResult {
  const criticalHit = input.degree === 'critical-hit';
  const spec = baseDamageDiceFor({
    baseDice: input.baseDice,
    rank5Dice: input.rank5Dice,
    rank: input.rank,
    criticalHit,
  });

  const components: DamageComponent[] = [];

  const base = rollDice(roller, spec);
  components.push({
    label: criticalHit ? `Base damage ${spec.count}d${spec.sides} (Critical: doubled dice)` : `Base damage ${spec.count}d${spec.sides}`,
    value: base.total,
    faces: base.faces,
  });

  if (input.damageStatMod !== null && input.damageStatAbbr !== null) {
    components.push({ label: `${input.damageStatAbbr} Mod`, value: input.damageStatMod });
  }

  // Rank damage die, when something grants one. Never doubled by a Critical Hit.
  if (input.grantsRankDamageDie && input.rankDamageDieTable) {
    const bonus = rankDamageBonus(input.rankDamageDieTable, input.rank);
    if (bonus && 'flat' in bonus) {
      components.push({ label: 'Rank damage bonus', value: bonus.flat });
    } else if (bonus && 'dice' in bonus) {
      const rolled = rollDice(roller, bonus.dice);
      components.push({
        label: `Rank damage die ${bonus.dice.count}d${bonus.dice.sides}`,
        value: rolled.total,
        faces: rolled.faces,
      });
    }
  }

  // RULE DCC-DMG-007 - Amazing Success adds bonus damage equal to F.
  if (input.degree === 'amazing-success') {
    components.push({ label: `Amazing Success (+F, Floor ${input.floor})`, value: input.floor });
  }

  for (const extra of input.extraComponents ?? []) components.push({ ...extra });

  return {
    baseDiceRolled: spec,
    components,
    total: components.reduce((acc, c) => acc + c.value, 0),
    damageType: input.damageType,
    criticalHit,
  };
}
